import { Injectable } from '@angular/core';

/**
 * One button the Alerts page contributes to its own tab line.
 *
 * The three state questions are functions rather than values on purpose. The page's mute
 * state moves on a load, on a write, and on a poll, and a descriptor of plain booleans
 * would have to be re-published at each of those — one missed call and the header draws a
 * button that is a step behind the page. Asked on every check instead, so there is nothing
 * to keep in step.
 */
export interface AlertsHeaderAction {
    id: string;
    label: string;
    /** A PrimeNG icon class, e.g. `pi pi-volume-off`. */
    iconClass: string;
    /** The button's tooltip. Says what pressing it does, not what it is called. */
    title: string;
    isVisible: () => boolean;
    isDisabled: () => boolean;
    run: () => void;
}

/**
 * The action the Alerts page puts on the line with its tabs, beside the time window.
 *
 * A service for the same reason the window is one: the tab strip is drawn by the app page
 * header and the state behind the button belongs to the routed page below it. Neither is
 * the other's parent, so an input and an output cannot carry it.
 *
 * The page owns everything the button does — the dialog, the write, the growler. This only
 * carries the offer. At most one action at a time: the header has room for one control
 * beside the window, and a second would want a layout decision this cannot make.
 *
 * The page must {@link clear} on the way out. Root-scoped, so an action left registered
 * would be drawn over whatever the reader navigated to.
 */
@Injectable({ providedIn: 'root' })
export class AlertsHeaderActionService {
    private current?: AlertsHeaderAction;

    /** The registered action while it wants to be seen, else undefined. */
    get action(): AlertsHeaderAction | undefined {
        if (!this.current?.isVisible()) {
            return undefined;
        }
        return this.current;
    }

    set(action: AlertsHeaderAction): void {
        this.current = action;
    }

    clear(): void {
        this.current = undefined;
    }
}
